// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * TreatmentPlanPanel - Treatment Plan & Home Instructions (vic/vetpo#14)
 *
 * Structured insulin plan, meal plan, "pet doesn't eat normally" exception
 * instructions, meal-reminder toggle and optional home instructions for a
 * diabetic pet, saved in ONE action ("Save & Send to Owner App") via
 * ada-core's vic/ada-core#192 upsert-by-pet endpoint (see treatmentPlanApi.ts).
 *
 * This replaces the insulin/meal HALF of what used to be a single free-text
 * Vet Notes box. Vet Internal Notes stays a SEPARATE component
 * (VetNotesPanel), its own resource (`emrs`), its own save action — this
 * panel's save payload is built exclusively from TreatmentPlanSavePayload
 * fields and has no field that could carry VetNotesPanel's note text (pinned by
 * "Save & Send never carries Vet Internal Notes content" in TreatmentPlanPanel.test.tsx).
 *
 * Hospital-level instruction templates that pre-fill the exception fields
 * are explicitly out of scope for this ticket (nice-to-have, not v1).
 */

import AddIcon from '@mui/icons-material/Add'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MedicationIcon from '@mui/icons-material/Medication'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { format, parseISO } from 'date-fns'
import React, { useEffect, useState } from 'react'
import { useGetList, useNotify, useRefresh, useStore, useTranslate } from 'react-admin'
import type { TreatmentPlan } from '../../types'
import {
  fetchInsulinProducts,
  InsulinProductOption,
  saveTreatmentPlan,
  TreatmentPlanSavePayload,
} from '../treatmentPlanApi'

export interface TreatmentPlanPanelProps {
  petId?: string | number
}

interface FormState {
  insulinProduct: string
  insulinDoseUnits: string
  insulinTimes: string[]
  mealTimes: string[]
  eatingLessInstructions: string
  notEatingInstructions: string
  mealReminderEnabled: boolean
  homeInstructions: string
}

const emptyForm: FormState = {
  insulinProduct: '',
  insulinDoseUnits: '',
  insulinTimes: [],
  mealTimes: [],
  eatingLessInstructions: '',
  notEatingInstructions: '',
  mealReminderEnabled: false,
  homeInstructions: '',
}

const formFromPlan = (plan: TreatmentPlan | undefined): FormState => ({
  insulinProduct: plan?.insulinProduct ?? '',
  insulinDoseUnits: plan?.insulinDoseUnits != null ? String(plan.insulinDoseUnits) : '',
  insulinTimes: (plan?.insulinTimes ?? []).map((t) => t.time),
  mealTimes: (plan?.mealTimes ?? []).map((t) => t.time),
  eatingLessInstructions: plan?.eatingLessInstructions ?? '',
  notEatingInstructions: plan?.notEatingInstructions ?? '',
  mealReminderEnabled: !!plan?.mealReminderEnabled,
  homeInstructions: plan?.homeInstructions ?? '',
})

const TimesEditor: React.FC<{
  times: string[]
  onChange: (times: string[]) => void
  addLabel: string
  removeLabel: string
}> = ({ times, onChange, addLabel, removeLabel }) => (
  <Box>
    {times.map((time, index) => (
      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <TextField
          type="time"
          size="small"
          value={time}
          onChange={(e) => {
            const next = [...times]
            next[index] = e.target.value
            onChange(next)
          }}
        />
        <IconButton
          size="small"
          aria-label={removeLabel}
          onClick={() => onChange(times.filter((_, i) => i !== index))}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    ))}
    <Button size="small" startIcon={<AddIcon />} onClick={() => onChange([...times, '08:00'])}>
      {addLabel}
    </Button>
  </Box>
)

// The list's plan unless the just-saved one is strictly newer. An unparseable
// timestamp on the list side cannot prove it caught up, so the saved plan wins.
const newerPlan = (
  listPlan: TreatmentPlan | undefined,
  savedPlan: TreatmentPlan | null,
): TreatmentPlan | undefined => {
  if (!savedPlan) return listPlan
  if (!listPlan) return savedPlan
  const listAt = Date.parse(listPlan.updatedAt)
  const savedAt = Date.parse(savedPlan.updatedAt)
  return Number.isFinite(listAt) && listAt >= savedAt ? listPlan : savedPlan
}

const TreatmentPlanEditor: React.FC<TreatmentPlanPanelProps> = React.memo(({ petId }) => {
  const translate = useTranslate()
  const refresh = useRefresh()
  const notify = useNotify()

  const [products, setProducts] = useState<InsulinProductOption[]>([])
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [isSaving, setIsSaving] = useState(false)
  const [exceptionsOpen, setExceptionsOpen] = useState(false)
  const [homeInstructionsOpen, setHomeInstructionsOpen] = useState(false)
  // The plan the last successful save returned. refresh() refetches the list,
  // but until that lands the list still holds the PRE-save plan: the summary
  // showed the old dose and Edit seeded the form from it, so a quick re-edit
  // + save wrote the old dose back to the owner's app (eng-brain pass 77).
  // Use whichever is newer; the list wins once it has caught up (or moved on).
  const [savedPlan, setSavedPlan] = useState<TreatmentPlan | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchInsulinProducts().then((list) => {
      if (!cancelled) setProducts(list)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const {
    data: plans = [],
    isLoading,
    error,
  } = useGetList<TreatmentPlan>(
    'treatment-plans',
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'updatedAt', order: 'DESC' },
      filter: petId ? { pet: petId } : {},
    },
    { enabled: !!petId },
  )
  const plan = newerPlan(plans[0], savedPlan)

  const startEdit = () => {
    setForm(formFromPlan(plan))
    setExceptionsOpen(!!(plan?.eatingLessInstructions || plan?.notEatingInstructions))
    setHomeInstructionsOpen(!!plan?.homeInstructions)
    setEditing(true)
  }

  const cancelEdit = () => {
    setEditing(false)
  }

  const matchedProduct = products.find((p) => p.value === form.insulinProduct)

  const handleSave = async () => {
    if (!petId) return
    setIsSaving(true)
    const payload: TreatmentPlanSavePayload = {
      insulinProduct: form.insulinProduct || null,
      insulinDoseUnits: form.insulinDoseUnits === '' ? null : Number(form.insulinDoseUnits),
      insulinTimes: form.insulinTimes.filter(Boolean).map((time) => ({ time })),
      mealTimes: form.mealTimes.filter(Boolean).map((time) => ({ time })),
      eatingLessInstructions: form.eatingLessInstructions.trim() || null,
      notEatingInstructions: form.notEatingInstructions.trim() || null,
      mealReminderEnabled: form.mealReminderEnabled,
      homeInstructions: form.homeInstructions.trim() || null,
    }
    const result = await saveTreatmentPlan(petId, payload)
    setIsSaving(false)
    if (result.ok) {
      setSavedPlan(result.plan)
      notify('pos.treatmentPlan.saved_successfully', { type: 'success' })
      refresh()
      setEditing(false)
    } else {
      notify(result.error || 'pos.treatmentPlan.error_saving', { type: 'error' })
    }
  }

  const formatUpdated = (timestamp: string): string => {
    try {
      return format(parseISO(timestamp), 'MMM d, yyyy HH:mm')
    } catch {
      return timestamp
    }
  }

  if (!petId) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {translate('pos.treatmentPlan.select_pet_first')}
        </Typography>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{translate('pos.treatmentPlan.error_loading')}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <MedicationIcon fontSize="small" />
        {translate('pos.treatmentPlan.title')}
      </Typography>

      {!editing && !plan && (
        <Paper sx={{ p: 2, mb: 1 }} variant="outlined">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {translate('pos.treatmentPlan.no_plan_yet')}
          </Typography>
          <Button size="small" variant="outlined" onClick={startEdit}>
            {translate('pos.treatmentPlan.create_plan')}
          </Button>
        </Paper>
      )}

      {!editing && plan && (
        <Paper sx={{ p: 2, mb: 1 }} variant="outlined">
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {plan.insulinProduct
              ? translate(
                  plan.insulinDoseUnits != null
                    ? 'pos.treatmentPlan.summary_insulin'
                    : 'pos.treatmentPlan.summary_insulin_no_dose',
                  {
                    dose: plan.insulinDoseUnits,
                    product:
                      products.find((p) => p.value === plan.insulinProduct)?.label ??
                      plan.insulinProduct,
                  },
                )
              : translate('pos.treatmentPlan.summary_no_insulin')}
          </Typography>
          {plan.insulinSyringeType && (
            <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 0.5 }}>
              ✓ {plan.insulinSyringeType}
            </Typography>
          )}
          {plan.insulinTimes && plan.insulinTimes.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {plan.insulinTimes.map((t) => t.time).join(', ')}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mb: 1 }}>
            {plan.mealTimes && plan.mealTimes.length > 0
              ? translate('pos.treatmentPlan.summary_meal_times', {
                  times: plan.mealTimes.map((t) => t.time).join(', '),
                })
              : translate('pos.treatmentPlan.summary_no_meal_times')}
          </Typography>
          <Chip
            size="small"
            label={translate(
              plan.mealReminderEnabled
                ? 'pos.treatmentPlan.summary_reminders_on'
                : 'pos.treatmentPlan.summary_reminders_off',
            )}
            sx={{ mb: 1 }}
          />
          {plan.homeInstructions && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {translate('pos.treatmentPlan.summary_home_instructions')}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {translate('pos.treatmentPlan.last_updated', { date: formatUpdated(plan.updatedAt) })}
          </Typography>
          <Button size="small" variant="outlined" onClick={startEdit}>
            {translate('pos.treatmentPlan.edit')}
          </Button>
        </Paper>
      )}

      {editing && (
        <Paper sx={{ p: 2, mb: 1, bgcolor: 'grey.50' }}>
          {/* Insulin Plan */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {translate('pos.treatmentPlan.insulin_plan_section')}
          </Typography>
          <TextField
            select
            size="small"
            fullWidth
            label={translate('pos.treatmentPlan.insulin_product_label')}
            value={form.insulinProduct}
            onChange={(e) => setForm((f) => ({ ...f, insulinProduct: e.target.value }))}
            disabled={isSaving}
            sx={{ mb: 1 }}
          >
            <MenuItem value="">
              {translate('pos.treatmentPlan.insulin_product_placeholder')}
            </MenuItem>
            {products.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          {matchedProduct && (
            <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 1 }}>
              {translate('pos.treatmentPlan.syringe_matched', {
                syringe: matchedProduct.syringeType,
              })}
            </Typography>
          )}
          <TextField
            type="number"
            size="small"
            fullWidth
            label={translate('pos.treatmentPlan.insulin_dose_label')}
            value={form.insulinDoseUnits}
            onChange={(e) => setForm((f) => ({ ...f, insulinDoseUnits: e.target.value }))}
            disabled={isSaving}
            inputProps={{ min: 0 }}
            sx={{ mb: 1 }}
          />
          <TimesEditor
            times={form.insulinTimes}
            onChange={(insulinTimes) => setForm((f) => ({ ...f, insulinTimes }))}
            addLabel={translate('pos.treatmentPlan.add_time')}
            removeLabel={translate('pos.treatmentPlan.remove_time')}
          />

          {/* Meal Plan */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            {translate('pos.treatmentPlan.meal_plan_section')}
          </Typography>
          <TimesEditor
            times={form.mealTimes}
            onChange={(mealTimes) => setForm((f) => ({ ...f, mealTimes }))}
            addLabel={translate('pos.treatmentPlan.add_time')}
            removeLabel={translate('pos.treatmentPlan.remove_time')}
          />

          {/* Exceptions - collapsed by default */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mt: 2 }}
            onClick={() => setExceptionsOpen((o) => !o)}
          >
            {exceptionsOpen ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
            <Typography variant="subtitle2">
              {translate('pos.treatmentPlan.exceptions_section')}
            </Typography>
          </Box>
          <Collapse in={exceptionsOpen}>
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                label={translate('pos.treatmentPlan.eating_less_label')}
                placeholder={translate('pos.treatmentPlan.eating_less_placeholder')}
                value={form.eatingLessInstructions}
                onChange={(e) => setForm((f) => ({ ...f, eatingLessInstructions: e.target.value }))}
                disabled={isSaving}
                sx={{ mb: 1 }}
              />
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                label={translate('pos.treatmentPlan.not_eating_label')}
                placeholder={translate('pos.treatmentPlan.not_eating_placeholder')}
                value={form.notEatingInstructions}
                onChange={(e) => setForm((f) => ({ ...f, notEatingInstructions: e.target.value }))}
                disabled={isSaving}
              />
            </Box>
          </Collapse>

          {/* Meal reminders */}
          <FormControlLabel
            sx={{ mt: 2, display: 'block' }}
            control={
              <Switch
                checked={form.mealReminderEnabled}
                onChange={(e) => setForm((f) => ({ ...f, mealReminderEnabled: e.target.checked }))}
                disabled={isSaving}
              />
            }
            label={translate('pos.treatmentPlan.meal_reminders_label')}
          />

          {/* Home instructions - collapsed by default */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mt: 1 }}
            onClick={() => setHomeInstructionsOpen((o) => !o)}
          >
            {homeInstructionsOpen ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
            <Typography variant="subtitle2">
              {translate('pos.treatmentPlan.home_instructions_section')}
            </Typography>
          </Box>
          <Collapse in={homeInstructionsOpen}>
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                placeholder={translate('pos.treatmentPlan.home_instructions_placeholder')}
                value={form.homeInstructions}
                onChange={(e) => setForm((f) => ({ ...f, homeInstructions: e.target.value }))}
                disabled={isSaving}
              />
            </Box>
          </Collapse>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button onClick={cancelEdit} disabled={isSaving}>
              {translate('pos.treatmentPlan.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} /> : null}
            >
              {translate('pos.treatmentPlan.save_and_send')}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  )
})

/**
 * PetShow's route keeps this panel mounted across /pets/A/show -> /pets/B/show
 * (the bell's alert links navigate in-app), so the editor is keyed by pet: an
 * open form — `editing` + pet A's draft — must not survive the switch, or
 * Save & Send POSTs pet A's dosing plan to pet B's owner app.
 */
export const TreatmentPlanPanel: React.FC<TreatmentPlanPanelProps> = React.memo(
  ({ petId: propPetId }) => {
    const [storePetId] = useStore<any>('selectedPetId')
    const petId = propPetId ?? storePetId
    return <TreatmentPlanEditor key={petId != null ? String(petId) : 'no-pet'} petId={petId} />
  },
)

export default TreatmentPlanPanel
